import React, { useEffect, useId, useMemo, useState } from "react";
import Loading from "./Loading";
import { t, locale } from "../i18n";
import MEDAL_GLYPHS from "../medalGlyphs";
import { announceMedals, showMedal } from "../medalEvents";

const API = process.env.REACT_APP_API_URL || "";

// Backend QUIZ_TYPES slugs -> translation key under "modes", in the lobby order
export const MODE_SLUGS = [
  "culture", "imposteur", "blindtest", "buzzer", "pictionary",
  "bombe", "battleroyale", "references", "pixelguess", "smashpass", "hemicycle", "undercover",
];

export function modeLabel(slug) {
  return MODE_SLUGS.includes(slug) ? t(`modes.${slug}`) : slug;
}

export function fmtDate(isoStr) {
  if (!isoStr) return "—";
  // SQLite datetime('now') is UTC with no suffix: mark it so local display is right
  return new Date(isoStr.replace(" ", "T") + "Z").toLocaleString(locale(), { dateStyle: "short", timeStyle: "short" });
}

// Shared loading of the account statistics (profile preview + dedicated page).
export function usePlayerStats(account, onExpired) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!account) return;
    fetch(`${API}/auth/me/stats`, { headers: { Authorization: `Bearer ${account.token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired && onExpired(); return; } // expired token -> log out
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t("common.loadError"));
        setStats(data);
        announceMedals(data.freshMedals);
      })
      .catch(e => setError(e.message === "Failed to fetch" ? t("common.serverUnreachable") : e.message));
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  return { stats, error };
}

export function successRateOf(stats) {
  return stats && stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : null;
}

const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)} %` : "—");

// Les huit cases de statistiques : générales, ou celles d'un mode. Smash or
// Pass n'a ni score ni victoire — on y compte les verdicts à la place.
export function statTiles(stats, mode, smash) {
  if (!stats) return [];
  if (mode === "smashpass") {
    const themes = smash || [];
    const sum = key => themes.reduce((n, th) => n + (th[key] || 0), 0);
    const m = (stats.byMode || []).find(x => x.mode === mode) || {};
    const total = sum("total");
    return [
      [m.games || 0, t("stats.games")],
      [themes.length, t("stats.smashThemes")],
      [total, t("stats.smashCards")],
      [sum("favs"), t("stats.smashFavs")],
      [sum("smash"), t("smash.smash")],
      [sum("pass"), t("smash.pass")],
      [pct(sum("smash"), total), t("stats.smashRate")],
      [m.lastPlayed ? fmtDate(m.lastPlayed).split(" ")[0] : "—", t("stats.lastPlayed")],
    ];
  }
  if (mode) {
    const m = (stats.byMode || []).find(x => x.mode === mode) || {};
    return [
      [m.games || 0, t("stats.games")],
      [m.wins || 0, t("stats.wins")],
      [pct(m.wins || 0, m.games || 0), t("stats.winRate")],
      [pct(m.correct || 0, m.total || 0), t("stats.correctAnswers")],
      [m.bestScore || 0, t("stats.bestScore")],
      [m.avgScore || 0, t("stats.avgScore")],
      [m.total || 0, t("stats.answersGiven")],
      [m.lastPlayed ? fmtDate(m.lastPlayed).split(" ")[0] : "—", t("stats.lastPlayed")],
    ];
  }
  return [
    [stats.games, t("stats.games")],
    [stats.wins, t("stats.wins")],
    [pct(stats.wins, stats.games), t("stats.winRate")],
    [pct(stats.correct, stats.total), t("stats.correctAnswers")],
    [stats.bestScore, t("stats.bestScore")],
    [stats.avgScore || 0, t("stats.avgScore")],
    [(stats.byMode || []).length, t("stats.modesPlayed")],
    [stats.total || 0, t("stats.answersGiven")],
  ];
}

export function StatTiles({ tiles }) {
  return (
    <div class="stats-tiles">
      {tiles.map(([value, label]) => (
        <div key={label} class="stats-preview-card">
          <span class="stats-preview-value">{value}</span>
          <span class="stats-preview-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

// Les thèmes tranchés au Smash or Pass, chargés à la demande.
export function useSmashThemes(account) {
  const [themes, setThemes] = useState(null);
  useEffect(() => {
    if (!account) return;
    fetch(`${API}/auth/me/smash`, { headers: { Authorization: `Bearer ${account.token}` } })
      .then(r => r.json()).then(d => setThemes(d.themes || [])).catch(() => setThemes([]));
  }, [account]);
  return themes;
}

// ══════════════════════════════════════════════════════════════════════════
// MÉDAILLES — elles se gravent, elles ne s'achètent pas (voir backend/index.js,
// MEDALS). Une famille = une silhouette, une rareté = un métal, d'après la
// maquette « Médailles ».
// ══════════════════════════════════════════════════════════════════════════
export const MEDAL_FAMILIES = [
  { id: "parcours", label: "medals.familyParcours" },
  { id: "modes", label: "medals.familyModes" },
  { id: "economie", label: "medals.familyEconomie" },
  { id: "tirage", label: "medals.familyTirage" },
  { id: "infamie", label: "medals.familyInfamie" },
  { id: "secretes", label: "medals.familySecretes" },
];
const FAMILY_LABEL = Object.fromEntries(MEDAL_FAMILIES.map(f => [f.id, f.label]));
export const medalFamilyLabel = (id) => t(FAMILY_LABEL[id] || id);

// Une médaille, d'après les maquettes : la plaque découpée à la silhouette de
// sa famille, l'anneau gravé, la gravure SVG au centre (planche « Glyphes »)
// et, sous la plaque, l'encadré du palier — même métal, collé au bord. Tout
// est en em : la taille du badge pilote le reste. Verrouillée, elle garde sa
// silhouette et perd son métal.
const TIER_MARKS = ["0", "I", "II", "III", "IV", "V"];

// Popumaniaque, la seule forme unique du catalogue (planche « Glyphes ») : une
// rosace à huit pans alternant orange, jaune et blanc satin, un disque satiné
// cerclé d'encre, « La Populasse » en couronne et le logo maison au centre.
// Verrouillée, la rosace reste mais noircie, sans texte ni logo : rien ne dit
// ce qu'elle est tant qu'on ne l'a pas.
const POPU_PANS = [
  ["M60 60L60 4Q73.8 26.7 99.6 20.4Z", "orange"], ["M60 60L99.6 20.4Q93.3 46.2 116 60Z", "sat"],
  ["M60 60L116 60Q93.3 73.8 99.6 99.6Z", "yellow"], ["M60 60L99.6 99.6Q73.8 93.3 60 116Z", "sat"],
  ["M60 60L60 116Q46.2 93.3 20.4 99.6Z", "orange"], ["M60 60L20.4 99.6Q26.7 73.8 4 60Z", "sat"],
  ["M60 60L4 60Q26.7 46.2 20.4 20.4Z", "yellow"], ["M60 60L20.4 20.4Q46.2 26.7 60 4Z", "sat"],
];
function PopuRosette({ locked }) {
  const uid = useId().replace(/:/g, "");
  const ink = locked ? "#0D121C" : "#232A3A";
  const fill = {
    orange: locked ? "#242C3E" : "#F58700EB",
    yellow: locked ? "#242C3E" : "#F8CF01EB",
    sat: locked ? "#1A2130" : `url(#sat${uid})`,
  };
  return (
    <svg class="medal-popu" viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <linearGradient id={`sat${uid}`} x1="0" y1="0" x2=".7" y2="1">
          <stop offset="0" stop-color="#FFFFFF" /><stop offset=".46" stop-color="#EEF2F7" />
          <stop offset=".54" stop-color="#FFFFFF" /><stop offset="1" stop-color="#E2E8F0" />
        </linearGradient>
        <radialGradient id={`disc${uid}`} cx=".34" cy=".26" r=".85">
          <stop offset="0" stop-color={locked ? "#2A3346" : "#FFFFFF"} />
          <stop offset=".6" stop-color={locked ? "#1E2535" : "#F4F7FB"} />
          <stop offset="1" stop-color={locked ? "#141A27" : "#DCE3EC"} />
        </radialGradient>
        <path id={`ring${uid}`} d="M60 60 m -23.5 0 a 23.5 23.5 0 1 1 47 0 a 23.5 23.5 0 1 1 -47 0" />
      </defs>
      {POPU_PANS.map(([d, tone]) => <path key={d} d={d} fill={fill[tone]} stroke={ink} stroke-width="2" />)}
      <circle cx="60" cy="60" r="34.5" fill={`url(#disc${uid})`} stroke={ink} stroke-width="2.4" />
      <circle cx="60" cy="60" r="18.5" fill="none" stroke={ink} stroke-width="1.2" opacity=".45" />
      {!locked && (
        <>
          <text stroke="none" style={{  fontFamily: "'Fredoka One', cursive", fontSize: "8.2px", letterSpacing: ".7px" }}>
            <textPath href={`#ring${uid}`} startOffset="25%" text-anchor="middle">LA POPULASSE</textPath>
          </text>
          <image href="/logo512.png" x="44" y="44" width="32" height="32" preserveAspectRatio="xMidYMid meet" />
        </>
      )}
    </svg>
  );
}

export function MedalBadge({ medal, size = 64, className = "" }) {
  const locked = !medal.unlocked;
  const hidden = locked && medal.secret;
  const glyph = MEDAL_GLYPHS[medal.glyph];
  const tiered = (medal.tierCount || 1) > 1 && !locked;
  return (
    <span class={`medal-badge ${className}`} data-family={medal.family} data-rarity={medal.rarity}
      data-medal={medal.glyph === "popumaniaque" ? "popu" : undefined}
      data-locked={locked || undefined} style={{ width: size, height: size, fontSize: `${size}px` }}>
      {!locked && (medal.rarity === "légendaire" || medal.rarity === "ultra") && <span class="medal-halo" />}
      {medal.glyph === "popumaniaque" ? <PopuRosette locked={locked} /> : (<>
      <span class="medal-plate" />
      <span class="medal-ring">
        {locked ? (
          <span class="medal-badge-glyph">?</span>
        ) : medal.glyph === "gnomed" ? (
          /* Gravure trop détaillée pour un trait unique : image découpée
             (mask-image), recolorée à l'encre du métal comme les autres. */
          <span class="medal-icon medal-icon-gnome" />
        ) : glyph ? (
          /* La planche de gravures est du balisage écrit dans le dépôt : rien
             ne vient d'une saisie ni du réseau. */
          <svg class="medal-glyph" viewBox="0 0 64 64" fill="none" stroke="currentColor"
            stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"
            dangerouslySetInnerHTML={{ __html: glyph }} />
        ) : (
          <span class="medal-badge-glyph">?</span>
        )}
      </span>
      </>)}
      {tiered && <span class="medal-tier">{TIER_MARKS[medal.tier] || "0"}</span>}
    </span>
  );
}

// La grille complète : recherche, regroupement par famille, sélection (jusqu'à
// cinq) pour l'encadré du profil. Réutilisée telle quelle dans l'onglet du
// profil et sur la page « Toutes les médailles ».
export function MedalGrid({ medals, query = "", equippedIds = [], onToggleEquip, badgeSize = 72 }) {
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byFamily = new Map(MEDAL_FAMILIES.map(f => [f.id, []]));
    for (const m of medals) {
      if (q) {
        const hidden = !m.unlocked && m.secret;
        const hay = hidden ? "" : `${m.name} ${m.cond || ""}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      (byFamily.get(m.family) || byFamily.set(m.family, []).get(m.family)).push(m);
    }
    return MEDAL_FAMILIES.map(f => ({ ...f, medals: byFamily.get(f.id) || [] })).filter(f => f.medals.length);
  }, [medals, query]);

  const full = !onToggleEquip ? false : equippedIds.length >= 5;

  return (
    <div class="medal-grid-wrap">
      <div class="medal-groups">
        {groups.map(f => (
          <div key={f.id} class="medal-group">
            <h4 class="medal-group-title">{t(f.label)}</h4>
            <div class="medal-group-grid">
              {f.medals.map(m => {
                const hidden = !m.unlocked && m.secret;
                const equipped = equippedIds.includes(m.id);
                const canToggle = !!onToggleEquip && m.unlocked && (equipped || !full);
                // Le clic ouvre la fiche ; épingler se fait depuis elle.
                const open = () => showMedal(m, canToggle ? {
                  label: equipped ? t("medals.unpin") : t("medals.pin"),
                  onClick: () => onToggleEquip(m.id),
                } : null);
                return (
                  <button key={m.id} type="button" class={`medal-cell${equipped ? " equipped" : ""}`} onClick={open}>
                    <MedalBadge medal={m} size={badgeSize} />
                    <span class="medal-cell-name">{hidden ? t("medals.secretName") : m.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!groups.length && <p class="stats-empty">{t("medals.noResults")}</p>}
      </div>
    </div>
  );
}

// Le pied statique de la liste : recherche à gauche, « voir toutes » à droite —
// toujours visible, jamais emporté par le défilement de la grille.
export function MedalGridFooter({ query, onQuery, onOpenFull }) {
  return (
    <div class="medal-grid-footer">
      <input type="search" class="medal-search" placeholder={t("medals.searchPlaceholder")}
        value={query} onChange={e => onQuery(e.target.value)} />
      {onOpenFull && (
        <button type="button" class="btn btn-primary medal-see-all" onClick={onOpenFull}>
          {t("medals.seeAll")}
        </button>
      )}
    </div>
  );
}

// L'encadré du profil : jusqu'à cinq médailles choisies, plus une pastille
// « +N » pour le reste — clique dessus pour aller les choisir dans l'onglet.
export function MedalsBox({ medals, equippedIds = [], onOpenTab }) {
  const byId = new Map((medals || []).map(m => [m.id, m]));
  const chosen = equippedIds.map(id => byId.get(id)).filter(Boolean);
  const unlockedCount = (medals || []).filter(m => m.unlocked).length;
  return (
    <div role="button" tabIndex={0} class="medals-box" onClick={onOpenTab}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenTab && onOpenTab(); } }}>
      {chosen.length ? (
        <div class="medals-box-row">
          {chosen.map(m => (
            <span key={m.id} class="medal-tap" onClick={e => { e.stopPropagation(); showMedal(m); }}>
              <MedalBadge medal={m} size={64} />
            </span>
          ))}
        </div>
      ) : (
        <p class="medals-box-empty">{t("medals.boxEmpty")}<br /><span>{t("medals.boxHint")}</span></p>
      )}
      {!!medals?.length && <span class="medals-box-count">{t("medals.count", { n: unlockedCount, total: medals.length })}</span>}
    </div>
  );
}

// Le tableau récapitulatif d'un thème, dans une fenêtre superposée.
export function SmashRecapModal({ account, themeId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/auth/me/smash/${themeId}`, { headers: { Authorization: `Bearer ${account.token}` } })
      .then(r => r.json()).then(setData).catch(() => setData({ cards: [] }));
  }, [account, themeId]);

  return (
    <div class="ps-smash-modal" onClick={onClose}>
      <div class="ps-smash-panel" onClick={e => e.stopPropagation()}>
        {!data ? <Loading /> : (
          <>
            <h3 class="ps-smash-title">{data.theme}</h3>
            <div class="sp-recap">
              {["smash", "pass"].map(col => (
                <div key={col} class={`sp-col sp-col-${col}`}>
                  <h3 class="sp-col-title">{t(`smash.${col}`)}</h3>
                  <div class="sp-col-list">
                    {(data.cards || []).filter(c => c.verdict === col).map(c => (
                      <figure key={c.id} class={`sp-mini${c.fav ? " fav" : ""}`}>
                        <img src={c.image} alt={c.title} />
                        {c.fav && <span class="sp-mini-fav">★</span>}
                        <figcaption>{c.title}</figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <button class="btn btn-primary" onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// Statistics panel of the profile: the eight key figures and the way to the
// full statistics page. Presentational: the profile loads the stats once.
function StatsPreview({ stats, onOpenFull }) {
  if (!stats) return <Loading label={t("stats.loading")} />;
  return (
    <div class="stats-preview">
      <h2 class="profile-panel-title stats-preview-title">{t("stats.title")}</h2>
      <StatTiles tiles={statTiles(stats)} />
      <button type="button" class="btn btn-primary stats-preview-more" onClick={onOpenFull}>{t("stats.openFull")}</button>
    </div>
  );
}

export default StatsPreview;
